import { ApplicationController } from '@aha-app/mvc';
import type WhiteboardController from '../../controllers/WhiteboardController';
import type { RecordId } from '../../shared-types';

export type RoadmapImportFormat = 'embedded' | 'records' | 'image';

interface State {
  importOption: RoadmapImportFormat | null;
}

export interface RoadmapImportModalControllerProps {
  whiteboardController: WhiteboardController;
  bookmarkInfo: RoadmapImportInfo;
}

export interface RoadmapImportInfo {
  bookmarkId: RecordId;
  availableFormats: RoadmapImportFormat[];
}

class RoadmapImportModalController extends ApplicationController<
  State,
  RoadmapImportModalControllerProps
> {
  static initialState: State = {
    importOption: null,
  };

  actionSelectOption(option: RoadmapImportFormat) {
    this.setState({ importOption: option });
  }

  get bookmarkId() {
    return this.props.bookmarkInfo.bookmarkId;
  }

  async actionConfirm() {
    const { importOption } = this.state;
    const { whiteboardController } = this.props;

    if (!this.bookmarkId || !importOption) {
      return;
    }

    if (importOption === 'embedded') {
      await whiteboardController.actionPlaceEmbeddedBookmark(this.bookmarkId);
    } else if (importOption === 'records') {
      if (!whiteboardController.props.addRecordsFromBookmark) {
        throw new Error(
          'addRecordsFromBookmark method is not available on WhiteboardController'
        );
      }

      await whiteboardController.props.addRecordsFromBookmark(this.bookmarkId);
    } else if (importOption === 'image') {
      await this.importAsImage(whiteboardController, this.bookmarkId);
    }

    this.actionClose();
  }

  actionClose() {
    this.props.whiteboardController.actionSetShowRoadmapImportModal(undefined);
  }

  private async importAsImage(
    controller: WhiteboardController,
    bookmarkId: RecordId
  ) {
    try {
      // Create a lightweight remote form for Rails and submit it by clicking
      // the submit. This ensures Rails does the work for us of evalling the
      // response JS.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/bookmarks/${bookmarkId}/whiteboards/create_from_image`;
      form.dataset.remote = 'true';
      form.acceptCharset = 'UTF-8';
      form.style.display = 'none';

      function addHiddenInput(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }

      // Add the Rails-required fields
      addHiddenInput('authenticity_token', window.csrfToken?.() || '');
      addHiddenInput('bookmark_id', bookmarkId);
      addHiddenInput('export_type', 'png');
      addHiddenInput('type', 'existing');
      addHiddenInput('whiteboard[id]', controller.props.whiteboardId);

      // Add a submit button
      const submitBtn = document.createElement('button');
      submitBtn.type = 'submit';
      submitBtn.id = 'submit-btn';
      form.appendChild(submitBtn);

      document.body.appendChild(form);
      submitBtn.click();

      setTimeout(() => form.remove(), 1000);
    } catch (error) {
      console.error('Error importing roadmap as image:', error);
      controller.actionShowMessage(
        'Failed to import roadmap as image',
        false,
        true,
        5000
      );
    }
  }
}

export default RoadmapImportModalController;
